import { useEffect, useMemo, useState } from 'react';
import { DialogContent, DialogActions } from '@mui/material';
import { ThemeButton } from '@shared/ui/buttons';
import { FreeCardsBadge } from '@shared/ui/parts';
import TabsComponent from '@shared/ui/TabsComponent';
import { useForm, useWatch } from 'react-hook-form';
import {
  BankSelect,
  BasicSelectInput,
  AmountInput,
  QuantityInput,
} from '@shared/ui/forms/inputs';
import { fetchAllGroups } from '@app/store/slices/myTeamSlice';
import { useSelector, useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { useCards } from '@app/api/cards/useCards';
import {
  validateServerErrors,
  validateTransactionLimit,
} from '@utils/validation';
import {
  formatBalanceUi,
  FRACTION_DIGITS,
  getShortage,
} from '@utils/balance-helpers';
import { RenderItemTeam } from '@shared/ui/forms/select-render';
import { fetchBankWallets } from '@app/store/slices/bankWalletsSlice';
import { roles } from '@shared/static/roles';
import { FormSum } from '@shared/ui/forms/display';
import { cardTypeLabels } from '@utils/static';

//todo: добавить переводы
// ! - у баланс карт нет предупреждения о недостающем балансе (убрать warning)

//-когда выхожу за лимит бесплатных карт начинаю считать платные карты
//-в зависимости от выбранного бина использовать из апи валет значение card_issue_fee умножаем на количество новых платных карт
// ('count'- 'count_free_card') * card_issue_fee_by_bin <= bin_balance
// стоимость валюта равняется валюте выбранного бина

//todo: обновить текст нехватки ресурсов для мембера

export default function CreateCardDialog({
  close,
  setSubtitle,
  setHint,
  data,
}) {
  const { user } = useSelector((state) => state.user);
  const isMember = user?.group_role === roles.TEAM_MEMBER;

  const {
    control,
    handleSubmit,
    setError,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      count: 1,
      bank_bin_id: data?.bank_bin_id || null,
      group_id: isMember ? user?.groups?.[0]?.id : null,
    },
  });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { bankWallets } = useSelector((state) => state.bankWallets);
  const { createCard } = useCards();
  const [cardType, setCardType] = useState(cardTypeLabels.postpaid);
  const { tariff } = useSelector((state) => state.tariff);
  const { allGroups } = useSelector((state) => state.my_team);
  const cardTypes = [
    { label: t('limit card'), value: cardTypeLabels.postpaid },
    // { label: t('Balance card') + ' prepaid', value: cardTypeLabels.prepaid },
  ];
  const countWatch = useWatch({ control, name: 'count' });
  const bankBinIdWatch = useWatch({ control, name: 'bank_bin_id' });
  const transactionLimitWatch = useWatch({
    control,
    name: 'transaction_limit',
  });
  const monthlyBalanceLimitWatch = useWatch({
    control,
    name: 'monthly_balance_limit',
  });

  useEffect(() => {
    if (transactionLimitWatch != null && monthlyBalanceLimitWatch != null) {
      trigger('transaction_limit');
    }
  }, [monthlyBalanceLimitWatch, transactionLimitWatch]);

  const selectedWallet = useMemo(
    () => bankWallets.find((x) => x.bank_bin_id === bankBinIdWatch),
    [bankWallets, bankBinIdWatch],
  );

  const freeCardsCount =
    (tariff?.count_free_card ?? 0) - (tariff?.count_free_card_used ?? 0);

  const cardsIssueFee =
    (Number(selectedWallet?.card_issue_fee) || 0) *
    Math.max(0, (Number(countWatch) || 0) - freeCardsCount);

  const notEnoughBalance =
    !selectedWallet || cardsIssueFee <= 0
      ? 0
      : getShortage(
          cardsIssueFee,
          Number(selectedWallet.balance) || 0,
          selectedWallet.currency,
        );

  useEffect(() => {
    if (!isMember) {
      dispatch(fetchAllGroups());
    }
    dispatch(fetchBankWallets());
  }, []);

  useEffect(() => {
    setSubtitle(
      <FreeCardsBadge text={`${freeCardsCount} ${t('free_cards')}`} />,
    );
    setHint(
      <ul>
        <li>
          <span className="fw-500">{t('limit card')}</span>{' '}
          {t('create_limit_card_note')}
        </li>
      </ul>,
    );
  }, [freeCardsCount, setSubtitle, setHint, t]);

  async function createCardHandler(formData) {
    try {
      const res = await createCard({
        ...formData,
        count: Number(formData.count),
        transaction_limit: Number(formData.transaction_limit),
        monthly_balance_limit: Number(formData.monthly_balance_limit),
      });
      if (res) close();
    } catch (err) {
      validateServerErrors(setError, err);
    }
  }

  return (
    <>
      <DialogContent>
        <div className="form">
          <TabsComponent
            fullWidth
            value={cardType}
            setValue={setCardType}
            options={cardTypes}
          />
          <BankSelect
            control={control}
            name="bank_bin_id"
            label={t('bank_bin')}
            notEnough={notEnoughBalance}
            data={bankWallets}
            error={errors.bank_bin_id}
            valueKey="bank_bin_id"
            isRequired
            isTransfer
          />
          <div className="form-group">
            <AmountInput
              integer
              isRequired
              control={control}
              name="transaction_limit"
              label={t('transaction_limit')}
              placeholder={`${t('max')} ${formatBalanceUi(tariff?.transaction_limit, null, FRACTION_DIGITS.INTEGER)}`}
              max={tariff?.transaction_limit}
              currency={selectedWallet?.currency || 'USD'}
              hint={`${t('max')} ${formatBalanceUi(tariff?.transaction_limit, selectedWallet?.currency, FRACTION_DIGITS.INTEGER)}`}
              error={errors.transaction_limit}
              validation={(value) =>
                validateTransactionLimit(value, monthlyBalanceLimitWatch)
              }
            />
            <AmountInput
              integer
              isRequired
              control={control}
              name="monthly_balance_limit"
              label={t('card_limit')}
              placeholder={`${t('max')} ${formatBalanceUi(tariff?.monthly_card_spent_limit, null, FRACTION_DIGITS.INTEGER)}`}
              max={tariff?.monthly_card_spent_limit}
              currency={selectedWallet?.currency || 'USD'}
              hint={`${t('max')} ${formatBalanceUi(tariff?.monthly_card_spent_limit, selectedWallet?.currency, FRACTION_DIGITS.INTEGER)}`}
              error={errors.monthly_balance_limit}
            />
          </div>
          <div className="form-group">
            <QuantityInput
              control={control}
              disabled={!bankBinIdWatch}
              name="count"
              label={t('quantity')}
              max={tariff?.available_cards_to_create}
              hint={`${t('max')} ${tariff?.available_cards_to_create} ${t('card(s)')}`}
              error={errors.count}
            />
            <FormSum
              text={formatBalanceUi(
                cardsIssueFee,
                selectedWallet?.currency,
                null,
                true,
              )}
              label={t('card_issue_fee')}
            />
          </div>

          <BasicSelectInput
            control={control}
            name="group_id"
            error={errors.group_id}
            label={t('team')}
            cleanable={false}
            placeholder={t('select_team')}
            Render={RenderItemTeam}
            isRequired
            searchable
            disabled={isMember}
            data={isMember ? user?.groups : allGroups}
          />
        </div>
      </DialogContent>
      <DialogActions className="create-card-actions">
        <p className="body-12r">
          <Trans
            i18nKey="create_card_agreement"
            components={{
              button: <span className="fw-500" />,
              link: <a href="#" className="primary-text" />,
            }}
          />
        </p>
        <div className="btns-toolbar">
          <ThemeButton
            startIcon="check"
            onClick={handleSubmit(createCardHandler)}
            loading={isSubmitting}
            disabled={notEnoughBalance > 0}
            minWidth
            block={false}
          >
            {t('create')}
          </ThemeButton>
        </div>
      </DialogActions>
    </>
  );
}
